"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Dialogue() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      full: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        full: boolean;
      };
      if (conditions.reduced) {
        gsap.set([".dl-hairline", ".dl-kicker", ".dl-line", ".dl-marker path", ".dl-cta", ".dl-caption"], {
          clearProps: "all",
          opacity: 1,
          y: 0
        });
        return;
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 75%",
          once: true
        },
        defaults: {
          ease: "power3.out"
        }
      });
      tl.from(".dl-kicker", {
        opacity: 0,
        y: 12,
        duration: 0.6
      }).from(".dl-hairline", {
        scaleX: 0,
        transformOrigin: "center center",
        duration: 0.7
      }, "-=0.3").from(".dl-line", {
        yPercent: 110,
        opacity: 0,
        duration: 1.1,
        stagger: 0.09,
        ease: "cubic-bezier(0.22,1,0.36,1)"
      }, "-=0.2").from(".dl-marker path", {
        drawSVG: undefined,
        strokeDashoffset: (i, el) => {
          const length = (el as SVGPathElement).getTotalLength();
          (el as SVGPathElement).style.strokeDasharray = `${length}`;
          return length;
        },
        duration: 0.9,
        ease: "power2.out"
      }, "-=0.2").from(".dl-cta", {
        y: 24,
        opacity: 0,
        duration: 0.7,
        stagger: 0.12
      }, "-=0.4").from(".dl-caption", {
        opacity: 0,
        y: 8,
        duration: 0.5
      }, "-=0.3");
    });
  }, {
    scope: container
  });
  return <section data-section-id="22998" id="ready-to-grow-your-search-visibility" data-nav="light" className="bg-background text-foreground">
      <div ref={container} className="mx-auto flex max-w-[1200px] flex-col items-center px-5 sm:px-8 lg:px-14" style={{
      paddingTop: "clamp(140px, 18vh, 220px)",
      paddingBottom: "clamp(140px, 18vh, 220px)"
    }}>
        {}
        <span className="dl-kicker font-sans text-xs font-medium uppercase text-muted-foreground" style={{
        letterSpacing: "0.18em"
      }}>
          Get started
        </span>

        {}
        <div className="dl-hairline mt-6 h-px w-10 bg-border" />

        {}
        <h2 className="mt-10 text-center font-serif font-normal text-foreground" style={{
        fontSize: "clamp(2.25rem, 4.5vw, 3.7rem)",
        letterSpacing: "-0.03em",
        lineHeight: 0.95
      }}>
          <span className="block overflow-hidden">
            <span className="dl-line inline-block">Sustainable growth</span>
          </span>
          <span className="block overflow-hidden">
            <span className="dl-line relative inline-block">
              starts with a{" "}
              <span className="relative inline-block italic">
                <span className="relative z-10">consultation</span>
                {}
                <svg className="dl-marker pointer-events-none absolute left-0 right-0 -bottom-1 z-0 h-[0.35em] w-full overflow-visible" viewBox="0 0 300 24" fill="none" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M4 15 C 40 8, 90 20, 140 12 S 230 6, 296 14" stroke="hsl(220, 80%, 30%)" strokeWidth="8" strokeLinecap="round" fill="none" style={{
                  opacity: 0.85
                }} />
                </svg>
              </span>
              .
            </span>
          </span>
        </h2>

        {}
        <div className="mt-14 flex flex-col items-center gap-4 sm:flex-row sm:gap-6" style={{
        marginTop: "clamp(40px, 6vh, 56px)"
      }}>
          <div className="dl-cta">
            <Button asChild className="h-auto rounded-md bg-primary px-7 py-4 text-[1.0625rem] font-semibold text-primary-foreground shadow-sm transition-colors duration-300 hover:bg-[hsl(220,85%,35%)]">
              <Link to="/ContactPage">
                Book a free session
                <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>

          <div className="dl-cta">
            <Link to="/CaseStudiesPage" className="group relative inline-flex items-center px-2 py-2 text-[1.0625rem] font-semibold text-foreground">
              <span className="relative">
                Explore my results
                <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-200 ease-out group-hover:scale-x-100" style={{
                backgroundColor: "hsl(220, 80%, 30%)"
              }} />
              </span>
            </Link>
          </div>
        </div>

        {}
        <p className="dl-caption mt-8 text-center font-sans text-[0.9375rem] font-medium text-muted-foreground" style={{
        letterSpacing: "0.06em"
      }}>
          I respond within 48 hours.
        </p>
        <p className="dl-caption mt-3 text-center font-sans text-[0.9375rem] font-medium text-muted-foreground" style={{
        letterSpacing: "0.06em"
      }}>
          Or explore tailored SEO guidance{" "}
          <Link to="/B2BProfessionalServicesPage" className="text-primary underline underline-offset-4 transition-colors hover:text-primary/80">
            by industry
          </Link>{" "}
          or{" "}
          <Link to="/UkCitiesAreasPage" className="text-primary underline underline-offset-4 transition-colors hover:text-primary/80">
            by location
          </Link>
          .
        </p>
      </div>
    </section>;
}